import connect from '../components/connect';
import React from 'react';
import Dialog from '../components/dialog.jsx';
import OnOffSwitch from '../components/on_off_switch.jsx';
import ColorSelector from '../components/color_selector.jsx';

import classnames from 'classnames';
import { routeToAllNotes } from '../actions/route';
import { getMarkup, getAnswerFor } from '../selectors/markup';
import { canAskQuestion, hideSharingQA } from '../selectors/current_user';
import {
    setNoteFields,
    openNote,
    receiveNoteError,
    receivePageError,
    closeNote,
} from '../actions/note';
import { selectPage } from '../selectors/content';
import { selectNoteId } from '../selectors/route_params';
import { update, deleteMarkup, newMarkup } from '../actions/markup';
import PropTypes from 'prop-types';
import R from 'ramda';
import { genKey, newNotification } from 'actions/notifications';
import { ICONS } from '../const';

function ValidationError({ error, id }) {
    if (error != null) {
        return (
            <div className="validation-error" id={id} role="alert" aria-invalid>
                <span className="aural">Invalid: </span>
                {error}
            </div>
        );
    }
    return <span id={id} />;
}
ValidationError.propTypes = {
    error: PropTypes.string,
    id: PropTypes.string,
};

const noteIsNew = note => !note.cid;

class NoteDialog extends React.Component {
    static propTypes = {
        canAskQuestion: PropTypes.bool,
        closeNote: PropTypes.func.isRequired,
        content: PropTypes.any.isRequired,
        deleteMarkup: PropTypes.func.isRequired,
        hideSharingQA: PropTypes.bool.isRequired,
        newMarkup: PropTypes.func.isRequired,
        newNotification: PropTypes.func.isRequired,
        note: PropTypes.object,
        noteError: PropTypes.string,
        openNote: PropTypes.func.isRequired,
        pageError: PropTypes.string,
        params: PropTypes.object.isRequired,
        receiveNoteError: PropTypes.func.isRequired,
        receivePageError: PropTypes.func.isRequired,
        rootState: PropTypes.object.isRequired,
        routeToAllNotes: PropTypes.func.isRequired,
        setNoteFields: PropTypes.func.isRequired,
        update: PropTypes.func.isRequired,
    };

    pageLabel = React.createRef();

    noteText = React.createRef();

    tags = React.createRef();

    selectedText = React.createRef();

    canceled = false;

    async componentDidMount() {
        const { openNote, rootState } = this.props;
        const noteId = selectNoteId(this.props.params);
        await openNote(noteId ? getMarkup(rootState, noteId) : null);
        const defaultFocusField = noteId ? this.noteText : this.pageLabel;
        defaultFocusField.current && defaultFocusField.current.focus();
    }

    componentDidUpdate() {
        const { pageError, noteError } = this.props;
        if (!this.blockFocusRedirect) {
            if (pageError) {
                this.pageLabel.current.focus();
            } else if (noteError) {
                this.noteText.current.focus();
            }
        } else {
            this.blockFocusRedirect = false;
        }
    }

    onDialogClose = () => {
        this.props.closeNote();
        this.props.routeToAllNotes({
            contentId: this.props.content.id,
        });
    };

    getPageNum = () => {
        const { value } = this.pageLabel.current;
        const { content } = this.props;

        const tmp = R.compose(
            R.head,
            R.filter(p => p.label === value)
        );
        const page = tmp(content.pages);

        if (page != null) {
            return page.pagenum;
        }
    };

    setColor = color => {
        const { setNoteFields } = this.props;
        this.blockFocusRedirect = true;
        setNoteFields({ color });
    };

    delete = () => {
        const { deleteMarkup, note } = this.props;
        deleteMarkup(note);
        this.onDialogClose();
    };

    onCancel = () => {
        if (!this.canceled) {
            this.canceled = true;
            this.props.newNotification(
                'Canceled',
                'No changes were made to your note',
                {
                    dismissable: false,
                    key: genKey(),
                    icon: ICONS.CANCEL,
                }
            );
        }
    };

    generateButtons = () => {
        const isNew = noteIsNew(this.props.note);

        const buttons = [
            {
                label: 'Cancel',
                dismiss: true,
                onClick: this.onCancel,
            },
            {
                label: isNew ? 'Add Note' : 'Save',
                primary: true,
                onClick: this.save,
            },
        ];

        if (!isNew) {
            buttons.push({
                label: 'Delete',
                className: 'delete cl-icon cl-icon-times',
                onClick: this.delete,
            });
        }

        return buttons;
    };

    isValid = () => {
        const validPage = this.validatePage();
        const validNote = this.validateNoteText();
        return validPage && validNote;
    };

    save = () => {
        const { update, newMarkup } = this.props;
        if (this.isValid()) {
            const { note, content } = this.props;
            const tags = this.tags.current.value.split(',').map(t => t.trim());

            const data = {
                color: note.color,
                is_question: note.is_question,
                note_text: this.noteText.current.value,
                tags,
            };

            if (!noteIsNew(note)) {
                update(note, data);
            } else {
                data.content_id = content.id;
                data.page = this.getPageNum();
                data.selection_text = this.selectedText.current.value;
                newMarkup(data);
            }
            this.onDialogClose();
        }
    };

    toggleQuestion = () => {
        const { is_question } = this.props.note;
        const { setNoteFields } = this.props;
        this.blockFocusRedirect = true;
        setNoteFields({ is_question: !is_question });
    };

    validateNoteText = () => {
        let error = null;
        const { receiveNoteError } = this.props;
        const {
            current: { value },
        } = this.noteText;
        if (!value || value.trim() === '') {
            error = 'Missing note text';
        }
        receiveNoteError(error);
        return !error;
    };

    validatePage = () => {
        const {
            pageLabel: { current },
        } = this;
        if (!current) {
            return true;
        }
        let error = null;
        const { value } = current;
        const { receivePageError } = this.props;
        if (!value || value.trim() === '') {
            error = 'Enter a page number to add this note';
        } else if (!this.getPageNum()) {
            error = 'Please enter a valid page number';
        }
        receivePageError(error);
        return !error;
    };

    renderPageFieldset = () => {
        // You can't edit the page of an existing note
        if (!noteIsNew(this.props.note)) return null;

        const { pageError } = this.props;

        return (
            <fieldset>
                <label
                    className={classnames({
                        'validation-error': !!pageError,
                    })}
                    htmlFor="_engage96"
                >
                    Page Number
                </label>
                <input
                    ref={this.pageLabel}
                    name="page_number"
                    list="page_numbers"
                    placeholder="Page Number"
                    type="text"
                    id="_engage96"
                    aria-required="true"
                    aria-describedby="_engage96_error"
                />
                <ValidationError error={pageError} id="_engage96_error" />
            </fieldset>
        );
    };

    renderQuestionSwitch = () => {
        if (this.props.canAskQuestion) {
            return (
                <fieldset>
                    <OnOffSwitch
                        position="left"
                        checked={this.props.note.is_question}
                        onChange={this.toggleQuestion}
                    >
                        Post this note as a question.
                    </OnOffSwitch>
                </fieldset>
            );
        }
    };

    renderSelectionText = () => {
        // You can't edit the selection_text of an existing note
        if (!noteIsNew(this.props.note)) return false;

        const { selected_text } = this.props.note;

        return (
            <fieldset>
                <legend className="aural">Cited Material</legend>
                <p className="aural">
                    Insert text or quote from the course material to support
                    your notation.
                </p>
                <label htmlFor="_engage98">
                    Add cited material
                    <span className="secondary">(Optional)</span>
                </label>
                <textarea
                    defaultValue={selected_text}
                    ref={this.selectedText}
                    name="cited_text"
                    aria-describedby="ariaCiteInstructions"
                    placeholder="Enter cited material..."
                    id="_engage98"
                />
            </fieldset>
        );
    };

    render() {
        const {
            hideSharingQA,
            noteError,
            note,
            note: { color, note_text, tags },
        } = this.props;
        const isNew = noteIsNew(note);

        return (
            <Dialog
                onClose={this.onDialogClose}
                title={isNew ? 'Add Note' : 'Edit Note'}
                buttons={this.generateButtons()}
            >
                <div
                    key={
                        // Without this key, this form sometimes renders before
                        // the data arrives. It renders with no note text, then
                        // when the data arrives it doesn't re-render because
                        // we're not using a controlled input. Using the cid
                        // here lets us throw away the old form when the note
                        // does arrive, which forces it to re-render.
                        isNew ? 'new' : note.cid
                    }
                    className="add-page-note"
                >
                    {this.renderPageFieldset()}
                    <fieldset>
                        <label
                            className={classnames({
                                'validation-error': !!noteError,
                            })}
                            htmlFor="_engage97"
                        >
                            Note
                        </label>
                        <textarea
                            defaultValue={note_text}
                            ref={this.noteText}
                            placeholder="Enter note..."
                            id="_engage97"
                            aria-required="true"
                            aria-describedby="_engage97_error"
                        />
                        {}
                        <ValidationError
                            error={noteError}
                            id="_engage97_error"
                        />
                    </fieldset>
                    {this.renderSelectionText()}
                    <fieldset>
                        <label htmlFor="_engage99">
                            Tags <span className="secondary">(Optional)</span>
                        </label>
                        <input
                            aria-describedby="aria-embed-instructions"
                            defaultValue={tags}
                            ref={this.tags}
                            name="note_tags"
                            placeholder="Enter tags..."
                            type="text"
                            id="_engage99"
                        />
                        <aside id="aria-embed-instructions">
                            <p className="secondary">
                                Separate tags with commas.
                            </p>
                        </aside>
                    </fieldset>
                    <fieldset>
                        <legend className="aural">Color label</legend>
                        <ColorSelector onChange={this.setColor} color={color} />
                    </fieldset>
                    {hideSharingQA ? null : this.renderQuestionSwitch()}
                </div>
            </Dialog>
        );
    }
}

const mapStateToProps = (state, props) => {
    let pageLabel = '';
    let answered = false;
    let tags = '';
    const { content } = props;
    const { data: note, error, pageError } = state.current_note;
    const isNew = noteIsNew(note);
    if (!isNew) {
        const page = selectPage(state, content, note.page);
        pageLabel = page.label;
        tags = note.tags.join(',');
    }
    if (!isNew && note.is_question) {
        answered = !!getAnswerFor(state, note);
    }
    return {
        hideSharingQA: hideSharingQA(state),
        rootState: state,
        canAskQuestion: !answered && canAskQuestion(state, content),
        note: {
            tags,
            pageLabel,
            ...note,
        },
        noteError: error,
        pageError,
    };
};

export default connect(
    mapStateToProps,
    {
        routeToAllNotes,
        openNote,
        closeNote,
        setNoteFields,
        receiveNoteError,
        receivePageError,
        newMarkup,
        deleteMarkup,
        update,
        newNotification,
    }
)(NoteDialog);



// WEBPACK FOOTER //
// ./src/controllerviews/note_dialog.jsx